/**
 * Camunda 7.18 Process Instance REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/process-instance/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  ProcessInstance,
  ProcessInstanceQuery,
  CountResult,
  ActivityInstance,
  Variables,
  VariableValue,
  PatchVariablesDto,
  ProcessInstanceModificationDto,
  DeleteProcessInstancesDto,
  SetJobRetriesDto,
  SetVariablesAsyncDto,
  CorrelationMessageDto,
  MessageCorrelationResult,
  Batch,
} from '../types';

export class ProcessInstanceService {
  private client: CamundaApiClient;
  private basePath = '/engine-rest/process-instance';

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Get List
  // ============================================================================

  /**
   * Get a list of process instances
   */
  async getList(query?: ProcessInstanceQuery): Promise<ProcessInstance[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<ProcessInstance[]>(`${this.basePath}${params}`);
  }

  /**
   * Get process instances count
   */
  async getCount(query?: ProcessInstanceQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/count${params}`);
  }

  /**
   * Get process instances using POST (for complex queries)
   */
  async queryList(query: ProcessInstanceQuery): Promise<ProcessInstance[]> {
    const { firstResult, maxResults, sortBy, sortOrder, ...body } = query;
    const params = this.buildPaginationParams({ firstResult, maxResults, sortBy, sortOrder });
    return this.client.post<ProcessInstance[]>(`${this.basePath}${params}`, body);
  }

  /**
   * Get process instances count using POST
   */
  async queryCount(query: ProcessInstanceQuery): Promise<CountResult> {
    return this.client.post<CountResult>(`${this.basePath}/count`, query);
  }

  // ============================================================================
  // Get Single
  // ============================================================================

  /**
   * Get process instance by ID
   */
  async getById(id: string): Promise<ProcessInstance> {
    return this.client.get<ProcessInstance>(`${this.basePath}/${id}`);
  }

  // ============================================================================
  // Activity Instance
  // ============================================================================

  /**
   * Get activity instance tree
   */
  async getActivityInstance(id: string): Promise<ActivityInstance> {
    return this.client.get<ActivityInstance>(`${this.basePath}/${id}/activity-instances`);
  }

  // ============================================================================
  // Variables
  // ============================================================================

  /**
   * Get all process variables
   */
  async getVariables(
    id: string,
    options?: { deserializeValues?: boolean }
  ): Promise<Variables> {
    const params = options?.deserializeValues !== undefined
      ? `?deserializeValues=${options.deserializeValues}`
      : '';
    return this.client.get<Variables>(`${this.basePath}/${id}/variables${params}`);
  }

  /**
   * Get a single process variable
   */
  async getVariable(
    id: string,
    varName: string,
    options?: { deserializeValue?: boolean }
  ): Promise<VariableValue> {
    const params = options?.deserializeValue !== undefined
      ? `?deserializeValue=${options.deserializeValue}`
      : '';
    return this.client.get<VariableValue>(`${this.basePath}/${id}/variables/${varName}${params}`);
  }

  /**
   * Get binary variable data
   */
  async getVariableBinary(id: string, varName: string): Promise<Blob> {
    return this.client.getBlob(`${this.basePath}/${id}/variables/${varName}/data`);
  }

  /**
   * Update/Delete process variables
   */
  async modifyVariables(id: string, dto: PatchVariablesDto): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/variables`, dto);
  }

  /**
   * Set a single process variable
   */
  async setVariable(id: string, varName: string, value: VariableValue): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/variables/${varName}`, value);
  }

  /**
   * Set binary variable data
   */
  async setVariableBinary(
    id: string,
    varName: string,
    data: Blob,
    valueType?: string
  ): Promise<void> {
    const formData = new FormData();
    formData.append('data', data);
    if (valueType) {
      formData.append('valueType', valueType);
    }
    await this.client.postFormData<void>(
      `${this.basePath}/${id}/variables/${varName}/data`,
      formData
    );
  }

  /**
   * Delete a process variable
   */
  async deleteVariable(id: string, varName: string): Promise<void> {
    await this.client.delete<void>(`${this.basePath}/${id}/variables/${varName}`);
  }

  // ============================================================================
  // Modification
  // ============================================================================

  /**
   * Modify process instance execution state
   */
  async modify(id: string, dto: ProcessInstanceModificationDto): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/modification`, dto);
  }

  /**
   * Modify process instance execution state async
   */
  async modifyAsync(id: string, dto: ProcessInstanceModificationDto): Promise<Batch> {
    return this.client.post<Batch>(`${this.basePath}/${id}/modification-async`, dto);
  }

  // ============================================================================
  // Suspension
  // ============================================================================

  /**
   * Activate/Suspend process instance by ID
   */
  async updateSuspensionStateById(id: string, suspended: boolean): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/suspended`, { suspended });
  }

  /**
   * Activate/Suspend multiple process instances
   */
  async updateSuspensionState(dto: {
    processInstanceIds?: string[];
    processInstanceQuery?: ProcessInstanceQuery;
    suspended: boolean;
  }): Promise<void> {
    await this.client.put<void>(`${this.basePath}/suspended`, dto);
  }

  /**
   * Activate/Suspend process instances async
   */
  async updateSuspensionStateAsync(dto: {
    processInstanceIds?: string[];
    processInstanceQuery?: ProcessInstanceQuery;
    suspended: boolean;
  }): Promise<Batch> {
    return this.client.post<Batch>(`${this.basePath}/suspended-async`, dto);
  }

  // ============================================================================
  // Delete
  // ============================================================================

  /**
   * Delete process instance by ID
   */
  async deleteById(
    id: string,
    options?: {
      skipCustomListeners?: boolean;
      skipIoMappings?: boolean;
      skipSubprocesses?: boolean;
      failIfNotExists?: boolean;
    }
  ): Promise<void> {
    const params = new URLSearchParams();
    if (options?.skipCustomListeners) params.append('skipCustomListeners', 'true');
    if (options?.skipIoMappings) params.append('skipIoMappings', 'true');
    if (options?.skipSubprocesses) params.append('skipSubprocesses', 'true');
    if (options?.failIfNotExists !== undefined) params.append('failIfNotExists', String(options.failIfNotExists));
    const queryString = params.toString();
    await this.client.delete<void>(`${this.basePath}/${id}${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Delete multiple process instances
   */
  async deleteMultiple(dto: DeleteProcessInstancesDto): Promise<void> {
    await this.client.post<void>(`${this.basePath}/delete`, dto);
  }

  /**
   * Delete multiple process instances async
   */
  async deleteMultipleAsync(dto: DeleteProcessInstancesDto): Promise<Batch> {
    return this.client.post<Batch>(`${this.basePath}/delete-async`, dto);
  }

  /**
   * Delete process instances by historic query async
   */
  async deleteHistoricQueryBasedAsync(dto: DeleteProcessInstancesDto): Promise<Batch> {
    return this.client.post<Batch>(`${this.basePath}/delete-historic-query-based`, dto);
  }

  // ============================================================================
  // Job Retries
  // ============================================================================

  /**
   * Set job retries for process instances
   */
  async setJobRetries(dto: SetJobRetriesDto): Promise<void> {
    await this.client.post<void>(`${this.basePath}/job-retries`, dto);
  }

  /**
   * Set job retries async
   */
  async setJobRetriesAsync(dto: SetJobRetriesDto): Promise<Batch> {
    return this.client.post<Batch>(`${this.basePath}/job-retries-async`, dto);
  }

  /**
   * Set job retries by historic query async
   */
  async setJobRetriesHistoricQueryBasedAsync(dto: SetJobRetriesDto): Promise<Batch> {
    return this.client.post<Batch>(`${this.basePath}/job-retries-historic-query-based`, dto);
  }

  // ============================================================================
  // Variables Async
  // ============================================================================

  /**
   * Set variables async
   */
  async setVariablesAsync(dto: SetVariablesAsyncDto): Promise<Batch> {
    return this.client.post<Batch>(`${this.basePath}/variables-async`, dto);
  }

  // ============================================================================
  // Message Correlation
  // ============================================================================

  /**
   * Correlate a message
   */
  async correlateMessage(dto: CorrelationMessageDto): Promise<MessageCorrelationResult[]> {
    return this.client.post<MessageCorrelationResult[]>('/engine-rest/message', dto);
  }

  // ============================================================================
  // Comments
  // ============================================================================

  /**
   * Get process instance comments
   */
  async getComments(id: string): Promise<any[]> {
    return this.client.get<any[]>(`${this.basePath}/${id}/comment`);
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: ProcessInstanceQuery): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else if (typeof value === 'object') {
          params.append(key, JSON.stringify(value));
        } else {
          params.append(key, String(value));
        }
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  private buildPaginationParams(params: {
    firstResult?: number;
    maxResults?: number;
    sortBy?: string;
    sortOrder?: string;
  }): string {
    const urlParams = new URLSearchParams();
    if (params.firstResult !== undefined) urlParams.append('firstResult', String(params.firstResult));
    if (params.maxResults !== undefined) urlParams.append('maxResults', String(params.maxResults));
    if (params.sortBy) urlParams.append('sortBy', params.sortBy);
    if (params.sortOrder) urlParams.append('sortOrder', params.sortOrder);
    const queryString = urlParams.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let processInstanceServiceInstance: ProcessInstanceService | null = null;

export function getProcessInstanceService(): ProcessInstanceService {
  if (!processInstanceServiceInstance) {
    processInstanceServiceInstance = new ProcessInstanceService();
  }
  return processInstanceServiceInstance;
}

export default ProcessInstanceService;
